import React from "react";
import { Box, IconButton, useMediaQuery, useTheme } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { ChevronRight } from "@mui/icons-material";
import { dt } from "../../../themes/redesignTokens";

export interface BreadcrumbItem {
  label: string;
  onClick?: () => void;
}

export interface FormPageLayoutProps {
  title: string;
  onBack: () => void;
  backLabel?: string;
  breadcrumbs?: BreadcrumbItem[];
  headerRight?: React.ReactNode;
  maxWidth?: string;
  children: React.ReactNode;
}

const FormPageLayout: React.FC<FormPageLayoutProps> = ({
  title,
  onBack,
  backLabel,
  breadcrumbs,
  headerRight,
  maxWidth = "720px",
  children,
}) => {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const contentPx = isPhone ? 2 : isTablet ? 3 : 4;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        fontFamily: dt.font,
        "& *:not(.MuiSvgIcon-root)": { fontFamily: dt.font },
        backgroundColor: dt.pageBg,
        backgroundImage: `radial-gradient(circle, ${dt.pageDotColor} 1px, transparent 1px)`,
        backgroundSize: "20px 20px",
      }}
    >
      {/* Header bar */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          px: contentPx,
          pt: 3,
          pb: 1,
          animation: "headerReveal 0.5s cubic-bezier(0.4,0,0.2,1) both",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth,
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
        <IconButton
          onClick={onBack}
          sx={{
            color: dt.textSecondary,
            "&:hover": { color: dt.blue, backgroundColor: dt.blueSoft },
          }}
        >
          <ArrowBackIcon />
        </IconButton>

        <Box sx={{ flex: 1 }}>
          <Box
            sx={{
              fontSize: { xs: "16px", md: "18px" },
              fontWeight: 700,
              color: dt.textPrimary,
            }}
          >
            {title}
          </Box>

          {breadcrumbs && breadcrumbs.length > 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                mt: 0.25,
                flexWrap: "wrap",
              }}
            >
              {breadcrumbs.map((crumb, index) => {
                const isLast = index === breadcrumbs.length - 1;
                return (
                  <React.Fragment key={index}>
                    <Box
                      onClick={!isLast ? crumb.onClick : undefined}
                      sx={{
                        fontSize: "12px",
                        fontWeight: 500,
                        color: isLast ? dt.textMuted : dt.blue,
                        cursor: isLast ? "default" : "pointer",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "160px",
                        "&:hover": isLast
                          ? {}
                          : { textDecoration: "underline" },
                      }}
                    >
                      {crumb.label}
                    </Box>
                    {!isLast && (
                      <ChevronRight
                        sx={{ fontSize: 14, color: dt.textMuted }}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </Box>
          )}

          {!breadcrumbs && backLabel && (
            <Box
              sx={{
                fontSize: "12px",
                fontWeight: 500,
                color: dt.textMuted,
                mt: 0.25,
              }}
            >
              {backLabel}
            </Box>
          )}
        </Box>

        {headerRight && <Box>{headerRight}</Box>}
        </Box>
      </Box>

      {/* Content area */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          px: contentPx,
          py: 3,
          pb: 12,
          animation: "fadeUp 0.5s cubic-bezier(0.4,0,0.2,1) 0.1s both",
        }}
      >
        <Box sx={{ width: "100%", maxWidth }}>{children}</Box>
      </Box>
    </Box>
  );
};

export default FormPageLayout;
